import React from "react";
import {
    Button,
    Card,
    Typography,
    Space,
    Divider,
    message,
} from "antd";
import {
    FiLayers,       // Plans
    FiZap,          // Pro
    FiStar,         // Elite
    FiTarget,       // Free
    FiCheck,        // Feature check
    FiCreditCard,   // Billing
    FiCalendar,     // Renewal
    FiTrendingUp,   // Upgrade
    FiShield,       // Secure
} from "react-icons/fi";
import { Link, useLocation } from "react-router-dom";
import Layout from "../../components/layout/Layout";
import { useSelector } from "react-redux";

const { Title, Text } = Typography;

const PLANS = [
    {
        id: "free",
        name: "Free",
        icon: <FiTarget />,
        blurb: `Try Predixio with core predictions.`,
        priceMonthly: 0,
        priceYearly: 0,
        cta: "Get started",
        highlight: `${["1X2 (Win/Draw/Lose)"].join(", ")} only`,
        bullets: [
            `Only ${["1X2 (Win/Draw/Lose)"].join(", ")}`,
            `Up to 100 predictions per user`,
            "Community support",
            "Email digests (weekly)",
        ],
        includes: { markets_1x2: true, markets_all: false, reasoning: true, live_inplay: false, accas: false, api: false, export: false },
    },
    {
        id: "pro",
        name: "Pro",
        icon: <FiZap />,
        blurb: "Unlock advanced markets and in-play. Best for serious bettors.",
        priceMonthly: 19, priceYearly: 190,
        cta: "Upgrade to Pro",
        highlight: "All markets + In-Play",
        bullets: [
            "All markets (BTTS, O/U, AH, Corners, Cards, Props)",
            "In-Play signals, Accas builder",
            "API access (rate-limited)",
            "Priority email support",
        ],
        includes: { markets_1x2: true, markets_all: true, reasoning: true, live_inplay: true, accas: true, api: true, export: true },
    },
    {
        id: "elite",
        name: "Elite",
        icon: <FiStar />,
        blurb: "For high-volume users and teams. Maximum limits and analytics.",
        priceMonthly: 49, priceYearly: 490,
        cta: "Go Elite",
        highlight: "Highest limits + Dedicated support",
        bullets: [
            "All Pro features",
            "Higher API rate & historical data",
            "Strategy reviews, onboarding call",
            "Dedicated support (fast SLA)",
        ],
        includes: { markets_1x2: true, markets_all: true, reasoning: true, live_inplay: true, accas: true, api: true, export: true },
    },
];

function Subnav() {
    const LINKS = [
        { to: "/dashboard", label: "Dashboard", Icon: FiTrendingUp },
        { to: "/my/predictions", label: "My predictions", Icon: FiTrendingUp },
        { to: "/notifications", label: "Notifications", Icon: FiTrendingUp },
        { to: "/account/profile", label: "My profile", Icon: FiTrendingUp },
        { to: "/account/billing", label: "Billing", Icon: FiCreditCard },
        { to: "/plans", label: "Plans & Pricing", Icon: FiLayers },
        { to: "/account/security", label: "Change password", Icon: FiShield },
    ];
    const { pathname } = useLocation();
    const isActive = (to) => pathname === to || (to !== "/" && pathname.startsWith(to));

    return (
        <nav className="px-subnav" aria-label="Account navigation">
            <div className="px-subnav__inner">
                {LINKS.map(({ to, label, Icon }) => (
                    <Link key={to} to={to} className={`px-subnav__link ${isActive(to) ? "is-active" : ""}`}>
                        <Icon aria-hidden />
                        <span>{label}</span>
                    </Link>
                ))}
            </div>
        </nav>
    );
}

function Feature({ children }) {
    return (
        <li className="px-feature">
            <FiCheck className="ok" /> <span>{children}</span>
        </li>
    );
}

export default function PlansPricing(props) {    
    const member = useSelector((state) => state.Member.data);
    const plans = useSelector((state) => state.Utility.plans);
    const activePlanCode = member?.subscription?.code || "free";
    const activePlan = plans.find((p) => p.code === activePlanCode) || PLANS[0];
    const nextRenewal = member?.subscription?.renewsAt || new Date(Date.now() + 1000 * 60 * 60 * 24 * 30).toISOString();
    const billingCycle = member?.subscription?.interval || "Monthly";

    const onUpgrade = (planCode) => {
        if (planCode === activePlan.code) return;
        message.loading({ content: "Redirecting to secure checkout…", key: "upg" });
        setTimeout(() => {
            message.success({ content: `Upgraded to ${planCode.toUpperCase()}!`, key: "upg", duration: 2.2 });
        }, 1000);
    };

    const ui = {
        heroWrap: {
            position: "relative",
            padding: "80px 16px 80px",
            background:
                "radial-gradient(1200px 280px at 50% -30%, rgba(59,130,246,0.25), transparent 60%), linear-gradient(180deg, #0b1020 0%, #0b1020 40%, #0c1428 100%)",
            color: "white",
            borderBottom: "1px solid rgba(255,255,255,.06)",
        },
        heroInner: { maxWidth: 1350, margin: "0 auto" },
        badge: {
            display: "inline-flex", alignItems: "center", gap: 8,
            fontSize: 12, color: "#93c5fd", background: "rgba(147,197,253,.10)",
            border: "1px solid rgba(147,197,253,.30)", padding: "6px 12px", borderRadius: 999,
        },
        heroTitle: { margin: "14px 0 10px", fontSize: 36, lineHeight: 1.15, fontWeight: 900, color: "#fff", letterSpacing: "-0.02em" },
        heroSub: { margin: 0, color: "rgba(255,255,255,.85)", maxWidth: 820 },
    };

    return (
        <Layout headerStyle={1} footerStyle={1} breadcrumbTitle="Plans & Pricing">
            <section style={ui.heroWrap} aria-label="Hero">
                <div style={ui.heroInner}>
                    <span style={ui.badge}><FiLayers /> Plans & Pricing</span>
                    <h1 style={ui.heroTitle}>Choose the plan that fits your goals</h1>
                    <p style={ui.heroSub}>
                        Review your current plan, manage billing, and upgrade when you need more power.
                    </p>
                </div>
            </section>

            <Subnav />

            <section className="px-plans">
                <div className="px-wrap">
                    <aside className="px-side">
                        <Card className="px-card" bordered>
                            <Space align="center" size={10} style={{ marginBottom: 6 }}>
                                <Text strong>Active plan</Text>
                            </Space>
                            <Title level={3} style={{ marginTop: 0 }}>
                                {activePlan.name}
                            </Title>

                            <div className="px-meta">
                                <div className="px-meta__row">
                                    <span><FiCreditCard /> Billing</span>
                                    <span>{billingCycle}</span>
                                </div>
                                <div className="px-meta__row">
                                    <span><FiCalendar /> Next renewal</span>
                                    <span>{formatDate(nextRenewal)}</span>
                                </div>
                            </div>

                            <Divider style={{ margin: "14px 0" }} />

                            <ul className="px-features">
                                {activePlan?.features?.map((f) => (
                                    <Feature key={f?.description}>{f?.description}</Feature>
                                ))}
                            </ul>

                            <Divider style={{ margin: "14px 0" }} />

                            <Space style={{ width: "100%", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
                                <Button type="primary">
                                    <Link to="/account/billing">
                                        Manage billing
                                    </Link>
                                </Button>
                            </Space>
                        </Card>
                    </aside>

                    <main className="px-main">
                        <div className="px-grid">
                            {plans.map((plan) => {
                                const active = plan.code === activePlan.code;
                                return (
                                    <Card
                                        key={plan.code}
                                        className={`px-tier ${active ? "is-active" : ""}`}
                                        bordered
                                        title={
                                            <div className="px-tier__title">
                                                <div className="px-tier__name">
                                                    <span>{plan.name}</span>
                                                </div>
                                            </div>
                                        }
                                        extra={plan.icon ? <span className="px-tier__icon">{plan.icon}</span> : null}
                                    >
                                        <div className="px-price">
                                            {plan.priceMonthly === 0 ? (
                                                <span className="px-price__free">Free</span>
                                            ) : (
                                                <>
                                                    <div className="px-price__amount">
                                                        ${plan.monthlyPrice}
                                                        <span className="px-price__interval">/mo</span>
                                                    </div>
                                                    <div className="px-price__secondary">or {plan.yearlyPriceText}</div>
                                                </>
                                            )}
                                        </div>

                                        <ul className="px-features" style={{ marginTop: 10 }}>
                                            {plan?.features?.map((f) => (
                                                <Feature key={f?.description}>{f?.description}</Feature>
                                            ))}
                                        </ul>

                                        <Divider />

                                        <Space style={{ width: "100%" }}>
                                            <Button
                                                type={active ? "default" : "primary"}
                                                icon={<FiTrendingUp />}
                                                disabled={active}
                                                onClick={() => onUpgrade(plan.code)}
                                                block
                                            >
                                                {active ? "Current plan" : "Get started"}
                                            </Button>
                                        </Space>
                                    </Card>
                                );
                            })}
                        </div>
                    </main>
                </div>

                <style>{`
          .px-plans { background:#f7f7f9; padding:30px; }
          .px-wrap {
            max-width: 1400px; margin: 0 auto;
            display: grid; gap: 20px;
            grid-template-columns: 420px 1fr;
          }
          @media (max-width: 1024px) {
            .px-wrap { grid-template-columns: 1fr; }
          }

          .px-card { border-radius:16px; }

          .px-meta { display:grid; gap:8px; margin-top:6px; }
          .px-meta__row {
            display:flex; align-items:center; justify-content:space-between;
            color:#334155; font-size:13px;
          }
          .px-meta__row span:first-child { display:flex; align-items:center; gap:8px; color:#475569; }

          .px-features { list-style:none; padding:0; margin:0; display:grid; gap:8px; }
          .px-feature { display:flex; align-items:center; gap:10px; font-size:13px; color:#475569; }
          .px-feature .ok { color:#16a34a; }

          .px-grid {
            display:grid; gap: 16px;
            grid-template-columns: repeat(3, minmax(0,1fr));
          }
          @media (max-width: 1024px) {
            .px-grid { grid-template-columns: repeat(2, minmax(0,1fr)); }
          }
          @media (max-width: 640px) {
            .px-grid { grid-template-columns: 1fr; }
          }

          .px-tier { transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease; }
          .px-tier:hover {
            transform: translateY(-3px);
            box-shadow: 0 12px 28px rgba(2,6,23,.08);
            border-color: rgba(2,6,23,.12);
          }
          .px-tier.is-active {
            border-color: rgba(37,99,235,.35);
            box-shadow: 0 12px 28px rgba(37,99,235,.10);
          }
          .px-tier__title {
            display:flex; align-items:center; justify-content:space-between; gap:10px;
          }
          .px-tier__name { display:flex; align-items:center; gap:10px; font-weight:900; letter-spacing:-.02em; }
          .px-dot { width:10px; height:10px; border-radius:999px; display:inline-block; }

          .px-tier__icon { opacity:.8; }

          .px-price { display:flex; align-items:baseline; gap:10px; }
          .px-price__free { font-weight:900; font-size:22px; color:#0f172a; }
          .px-price__amount { font-size:28px; font-weight:900; color:#0f172a; line-height:1; }
          .px-price__interval { font-size:13px; color:#64748b; margin-left:4px; }
          .px-price__secondary { font-size:12px; color:#64748b; }

          /* Account subnav (same style as your Change Password page) */
          .px-subnav {
            position: sticky; top: 0; z-index: 20;
            backdrop-filter: blur(6px);
            background: rgba(255,255,255,.85);
            border-bottom: 1px solid rgba(2,6,23,.08);
          }
          .px-subnav__inner {
            max-width: 1400px; margin: 0 auto;
            padding: 10px 16px;
            display: flex; gap: 8px; align-items: center; flex-wrap: nowrap;
            overflow-x: auto; -webkit-overflow-scrolling: touch;
          }
          .px-subnav__link {
            display: inline-flex; align-items: center;
            padding: 8px 12px; gap: 8px;
            border-radius: 999px;
            font-size: 13px; font-weight: 700;
            color: #334155;
            border: 1px solid transparent;
            white-space: nowrap;
          }
          .px-subnav__link:hover {
            background: #f1f5f9;
            border-color: rgba(2,6,23,.08);
          }
          .px-subnav__link.is-active {
            color: #1e3a8a;
            background: rgba(37,99,235,.10);
            border-color: rgba(37,99,235,.25);
          }
          @media (max-width: 480px) {
            .px-subnav__inner { gap: 6px; padding: 8px 12px; }
            .px-subnav__link { padding: 6px 10px; font-weight: 600; }
          }
        `}</style>
            </section>
        </Layout>
    );
}

function formatDate(iso) {
    try {
        const d = new Date(iso);
        return d.toLocaleDateString([], { year: "numeric", month: "short", day: "2-digit" });
    } catch {
        return "—";
    }
}
